/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit, Input, Output } from '@angular/core';

import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';

import { CacheService } from '../../../global/ng2-cache/services/cache.service';
import { Partner } from '../../../global/constants';
import { PartnerField, AutomateTask } from '../../../global/models';
import { AccountService } from '../../../services/account.service';
import { ClassifierService } from '../../../services/classifier.service';
import { GlobalMessageService } from '../../../services/global-message.service';
import { AlertDialogComponent } from '../../../global/alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from '../../../global/confirm-dialog/confirm-dialog.component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap';

@Component({
  selector: 'app-respond-task',
  templateUrl: './respond-task.component.html'
})
export class RespondTaskComponent implements OnInit {
  @Input() task: AutomateTask;
  @Output() onChange = new Subject<AutomateTask>();
  @Output() onNavChange = new Subject<boolean>();

  session: any;
  loadingPage: boolean = false;

  // cc stuff
  showCC: any = {};
  addingStatic: boolean = false;
  newStatic: string = '';
  ccDialogRef: BsModalRef;

  classifier: any = null;
  inboxes: any[] = [];

  // form meta data
  actionTypes: any[] = [
    {
      name: 'send',
      description: 'Send Response'
    },
    {
      name: 'draft',
      description: 'Create Draft for Review'
    }
  ];

  sendDelay: any[] = [
    {
      value: 0,
      description: 'No delay'
    },
    {
      value: 5,
      description: '5 minutes'
    },
    {
      value: 15,
      description: '15 minutes'
    }
  ];

  // form state management
  availableCategory: any[] = [];
  unusedCategories: boolean = true;

  mceOptions = {
    branding: false,
    height: 200,
    menubar: false,
    plugins: 'lists link',
    statusbar: false,
    toolbar: 'fontsizeselect bold italic bullist numlist link',
    forced_root_block: ''
  };

  constructor(private cacheService: CacheService,
    private globalMessageService: GlobalMessageService,
    private accountService: AccountService,
    private classifierService: ClassifierService,
    private modalService: BsModalService) { }

  ngOnInit() {
    // should already be set from skill-detail
    this.session = this.cacheService.get('session');
    this.initView();
  }

  addResponseOption() {
    let cat: any;
    // find the first unused category and push it into the state array
    for (let idx = 0; idx < this.classifier.category.length; idx++) {
      if (this.task.settings.categories.findIndex(category => { return category.categoryId == this.classifier.category[idx].id }) < 0) {
        cat = this.classifier.category[idx];
        break;
      }
    }

    this.task.settings.categories.push(this.newCategory(cat.id, this.actionTypes[0].name));

    this.setupCategories();
    this.stage();
  }

  removeResponseOption(index: number) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `Confirm you'd like to remove the selected response`,
        confirm: function () {
          me.task.settings.categories.splice(index, 1);
          me.setupView();
          me.stage();
          me.globalMessageService.message(`Response was removed`);
        }
      }
    }));
  }

  changeInbox() {
    this.stage();
  }

  changeMeta() {
    this.setupCategories();
    this.stage();
  }

  changeContent() {
    this.stage();
  }

  hasCC(response) {
    return response.cc && (response.cc.static.length > 0 || response.cc.lead_owner.field);
  }

  getCCText(response) {
    let text = '';
    if (response.cc) {
      text = response.cc.static.map(recipient => recipient.email_address).join(', ');
      if (response.cc.lead_owner) {


        if (response.cc.lead_owner.field) {
          if (text.length > 0) {
            text = `${text} AND `;
          }
          text = `${text} the ${response.cc.lead_owner.field.display} from ${response.cc.lead_owner.partner.display}`;
        }
      }
      return text;
    }
  }

  private initView() {
    this.loadingPage = true;
    Observable.forkJoin(
      this.classifierService.getClassifier(this.task.settings.classifier_id),
      this.accountService.getBots()
    ).subscribe(
      (results: any) => {
        this.classifier = results[0];
        this.inboxes = results[1];

        // set up available categories (note this gets done again post setupView to get rid of used items)
        this.availableCategory = JSON.parse(JSON.stringify(this.classifier.category));
        this.availableCategory.forEach(ac => { ac.used = false });

        this.setupView();
        this.validate();
        this.loadingPage = false;
      },
      e => {
        this.globalMessageService.error();
      }
    );
  }

  private setupView() {
    if (this.task.settings.categories.length == 0) {
      // add a default response category if none have been created yet
      this.task.settings.categories.push(this.newCategory(this.availableCategory[0].id, this.actionTypes[0].name));
    }

    this.setupCategories();
  }

  private newCategory(id, name) {
    return {
      categoryId: id,
      action: name,
      delay: 0,
      content: '',
      cc: {
        static: [],
        lead_owner: {
          fallback: [],
          partner: null,
          field: null
        }
      }
    }
  }

  /**
   * Sets up categories for availability in dropdowns
   */
  private setupCategories() {
    // start with a baseline of available categories
    this.availableCategory = JSON.parse(JSON.stringify(this.classifier.category));
    this.availableCategory.forEach(ac => { ac.used = false });

    // remove any that are already used
    this.task.settings.categories.forEach(response => {
      let index = this.availableCategory.findIndex(ac => { return ac.id == response.categoryId });
      if (index >= 0) {
        this.availableCategory[index].used = true;
      }
    });

    // no more categories left, don't show the Add Response button
    if (this.classifier.category.length == this.task.settings.categories.length) {
      this.unusedCategories = false;
    }
    else {
      this.unusedCategories = true;
    }
  }

  /**
   * Validate task requirements are met and set
   */
  private validate() {
    this.task.enabled = true;
    this.task.valid = true;
    this.task.invalid_reason = '';

    if (!this.task.settings.inbox_id) {
      this.task.valid = false;
      this.task.invalid_reason = `An inbox must be selected to enable this task`;
    }
    else {
      let ib = this.inboxes.find(i => { return i._id == this.task.settings.inbox_id });

      if (!ib) {
        this.task.valid = false;
        this.task.invalid_reason = `The inbox selected for this task could not be found`;
        return
      }
      this.task.settings.partner = ib.type;
      this.task.settings.categories.forEach(cat => {
        cat.delay = parseInt(cat.delay);
        // check for blank content after removing spacers and <p> open/close for the <p>&nbsp;</p> case
        let tempContent = cat.content.replace(/&nbsp;|<p>|<\/p>/g, '').trim();
        if (tempContent.length == 0) {
          this.task.valid = false;
          this.task.invalid_reason = `Email template for active response(s) can't be blank`;
        }

        if (cat.cc.lead_owner.partner != null) {
          if (cat.cc.lead_owner.field == null) {
           this.task.invalid_reason = `Lead Owner field for ${cat.cc.lead_owner.partner.display} must be set`;
          }
          if (cat.cc.lead_owner.fallback.length == 0) {
            this.task.invalid_reason = `At least one fallback address must be set`;
          }
        }

      });
    }

  }

  /**
   * Stage changes to be saved
   */
  private stage() {
    this.validate();
    this.task.updated_at = new Date();
    this.onChange.next(this.task);
  }

  openCCDialog(dialog, response) {
    this.ccDialogRef = this.modalService.show(dialog, { class: 'modal-lg' });
    this.checkCCValid(response);
  }

  saveCCSettngs(response) {
    if (this.checkCCValid(response)) {
      response.cc.lead_owner.invalid_reason = null;
      this.stage();
      this.ccDialogRef.hide();
    }
    else {
      this.task.invalid_reason = response.cc.lead_owner.invalid_reason;
    }
  }

  checkCCValid(response) {
    if (response.cc.lead_owner.partner != null) {
      if (response.cc.lead_owner.field == null) {
        response.cc.lead_owner.invalid_reason = `Lead Owner field for ${response.cc.lead_owner.partner.display} must be set`;
      }
      if (response.cc.lead_owner.fallback.length == 0) {
        response.cc.lead_owner.invalid_reason = `At least one fallback address must be set`;
      }
    }

    if (response.cc.lead_owner.invalid_reason) {
      return false;
    }
    return true;
  }

 /**
  * Add an email address to the static list of email addresses for CCing
  */
  addStaticCC(response: any) {
    if (response.cc.static.findIndex(s => { return s.email_address == this.newStatic; }) >= 0) {
      // duplicate - don't allow
      this.modalService.show(AlertDialogComponent, Object.assign({}, {
        initialState: {
          message: `${this.newStatic} is already on your list of specific people.`,
          confirmBtn: 'Ok'
        }
      }));
      this.newStatic = '';
      this.addingStatic = false;
    }

    // not a duplicate - add to list
    response.cc.static.push({ email_address: this.newStatic, created_at: new Date() });

    // keep them sorted
    response.cc.static.sort((a: any, b: any) => {
      if (a.email_address < b.email_address) {
        return -1;
      }
      else {
        return 1;
      }
    });
    this.newStatic = '';
    this.addingStatic = false;
    this.stage();
  }

  clearLOSettings(response) {
    response.cc.lead_owner = { partner: null, field: null, fallback: [] };
    this.stage();
  }

  removeStaticCC(emailAddress: string, response: any) {
    response.cc.static.splice(response.cc.static.findIndex(s => { return s.email_address == emailAddress; }), 1);
    this.stage();
  }

 /**
  * Event from partner-leadowner-picker when the Partner dropdown changes
  * @param partner
  */
  changeLeadOwnerPartner(partner: Partner, response: any) {
    // Partner was reset
    if (!partner) {
      response.cc.lead_owner = {
        fallback: [],
        partner: null,
        field: null
      };
    }
    else {
      response.cc.lead_owner.partner = partner;
    }
    this.stage();
  }

 /**
  * Event from partner-leadowner-picker when the Field dropdown changes
  * @param field
  */
  changeLeadOwnerField(field: PartnerField, response: any) {
    response.cc.lead_owner.field = field;
    this.stage();
  }

  /**
   * Event from partner-leadowner-picker when the fallback addresses change
   * @param addresses
   */
  changeLeadOwnerFallback(addresses: Array<string>, response: any) {
    response.cc.lead_owner.fallback = addresses;
    this.stage();
  }

}



// WEBPACK FOOTER //
// ./client/app/automate/tasks/respond-task/respond-task.component.ts